import OrderStore from '../stores/order-store'
import ImageStore from '../stores/image-store'
import EditorOutputCreator from './editor-output-creator'
import { uploadPreview } from './preview-uploader'
import Config from '../config'

var PREVIEW_MAX_PX = 800;

// Cached so previews pre-warmed on upsale are reused at checkout. Resolves to a map of
// orderItemId -> PreviewImageID (regId). Never rejects.
var previewUploadsPromise = null;

// Drop the cache on any order change so edits after generation don't leave a stale thumbnail.
export function invalidatePreviewUploads() {
    previewUploadsPromise = null;
}

OrderStore.addChangeListener(invalidatePreviewUploads);

// Pre-warm rendering+uploading one preview per cart line (call on upsale, reuse at checkout).
// Idempotent: if a run is already in flight (or done), reuse it instead of restarting the work
// (the upsale "next" handler can fire more than once).
export function startPreviewUploads() {
    if (previewUploadsPromise) {
        return previewUploadsPromise;
    }

    previewUploadsPromise = Promise.resolve()
        .then(generateAndUploadLinePreviews)
        .catch(function (error) {
            logToCentralServer("Failed to generate cart-line previews", error, Config.LogLevel.WARN);
            return {};
        });

    return previewUploadsPromise;
}

// Reuse the pre-warmed uploads, or start them if none are in flight (e.g. /finished reached directly).
export function getPreviewUploads() {
    return previewUploadsPromise || startPreviewUploads();
}

// One preview per distinct cart line = (product + chosen options). Maps each order item to its line's
// PreviewImageID, keyed by order-item id (not imageId) so a photo duplicated across lines isn't
// overwritten. Per-line failures are swallowed.
function generateAndUploadLinePreviews() {
    OrderStore.sortItemsByImageTakenDate();
    var items = OrderStore.getItems() || [];
    if (!items.length) {
        return Promise.resolve({});
    }

    var creator = new EditorOutputCreator();
    var keyToUpload = {};   // lineKey -> Promise<PreviewImageID|undefined>
    var itemIdToKey = {};   // orderItemId -> lineKey

    items.forEach(function (orderItem) {
        // createImageEntry can throw synchronously (missing image, filter errors); skip the bad item
        // rather than abort every line's preview.
        var entry;
        try {
            entry = creator.createImageEntry(orderItem);
        } catch (error) {
            logToCentralServer("Failed to build cart-line preview entry", error, Config.LogLevel.WARN);
            return;
        }

        var optionsKey = (entry.options || [])
            .map(function (option) { return option.vendorArticleOptionID; })
            .sort()
            .join(',');
        var lineKey = entry.product + '|' + optionsKey;
        itemIdToKey[orderItem.getId()] = lineKey;

        if (!keyToUpload[lineKey]) {
            keyToUpload[lineKey] = generatePreviewFileForItem(orderItem, entry)
                .then(uploadPreview)
                .catch(function (error) {
                    logToCentralServer("Failed to generate/upload cart-line preview", error, Config.LogLevel.WARN);
                    return undefined;
                });
        }
    });

    var lineKeys = Object.keys(keyToUpload);
    return Promise.all(lineKeys.map(function (lineKey) {
        return keyToUpload[lineKey].then(function (id) { return { lineKey: lineKey, id: id }; });
    })).then(function (results) {
        var keyToId = {};
        results.forEach(function (result) { if (result.id) { keyToId[result.lineKey] = result.id; } });

        var itemIdToPreviewId = {};
        Object.keys(itemIdToKey).forEach(function (itemId) {
            var id = keyToId[itemIdToKey[itemId]];
            if (id) { itemIdToPreviewId[itemId] = id; }
        });
        return itemIdToPreviewId;
    });
}

// Render a flat JPEG of one print (edits baked in). editByUrl bakes effects/rotate but NOT the
// print-aspect crop, so that is composited on a canvas here (mirrors the on-screen preview).
function generatePreviewFileForItem(orderItem, entry) {
    var opeImage = ImageStore.getImageById(orderItem.getImageId());
    if (!opeImage) {
        return Promise.reject(new Error('No image found for order item'));
    }

    var previewFilter = buildPreviewFilter(entry.filter);
    var customSettings = orderItem.getCustomSettings();
    var isPolaroid = Boolean(customSettings && customSettings.polaroid);

    return opeImage.getSrcWithFilter(PREVIEW_MAX_PX, previewFilter).then(function (objectUrl) {
        if (isPolaroid) {
            return compositePolaroidPreview(objectUrl, orderItem, entry.filter.crop);
        }
        return compositeFlatPreview(objectUrl, orderItem, entry.filter.crop);
    });
}

// Geometry for a flat print preview: an output canvas at the product aspect, plus the source
// rectangle to draw into it. When a crop rect is present (percentages of the effected source the
// EditServer returned) it is used verbatim; otherwise the source is centre-cropped ("cover").
export function computeFlatPreviewLayout(productWidth, productHeight, imgWidth, imgHeight, crop, maxPx) {
    if (!productWidth || !productHeight || !imgWidth || !imgHeight) {
        throw new Error('Cannot composite print preview: missing product/image dimensions');
    }

    var outerAspect = productWidth / productHeight;
    var outerW = outerAspect >= 1 ? maxPx : Math.round(maxPx * outerAspect);
    var outerH = outerAspect >= 1 ? Math.round(maxPx / outerAspect) : maxPx;

    var hasCrop = Boolean(crop && (crop.x > 0 || crop.y > 0 || crop.w < 100 || crop.h < 100));

    if (hasCrop) {
        // Cap sx/sy one pixel short of the edge so at least 1px of source remains (a crop rect at the
        // extreme edge, e.g. crop.x===100, would otherwise give sw/sh of 0 and throw in drawImage).
        var sx = clamp((crop.x / 100) * imgWidth, 0, imgWidth - 1);
        var sy = clamp((crop.y / 100) * imgHeight, 0, imgHeight - 1);
        return {
            outerW: outerW,
            outerH: outerH,
            sx: sx,
            sy: sy,
            sw: clamp((crop.w / 100) * imgWidth, 1, imgWidth - sx),
            sh: clamp((crop.h / 100) * imgHeight, 1, imgHeight - sy)
        };
    }

    // No crop rect: centre-crop (cover) the source to the product aspect.
    var imgAspect = imgWidth / imgHeight;
    var sw = imgWidth;
    var sh = imgHeight;
    if (imgAspect > outerAspect) {
        sw = Math.max(1, imgHeight * outerAspect);
    } else {
        sh = Math.max(1, imgWidth / outerAspect);
    }

    return { outerW: outerW, outerH: outerH, sx: (imgWidth - sw) / 2, sy: (imgHeight - sh) / 2, sw: sw, sh: sh };
}

// Bake the print-aspect crop into a flat JPEG at the product aspect (see computeFlatPreviewLayout).
function compositeFlatPreview(objectUrl, item, crop) {
    return loadImage(objectUrl).then(function (photo) {
        // Print orientation follows the photo: a portrait photo flips the print to portrait, so swap
        // the product dimensions — otherwise a portrait photo is squashed into a landscape canvas.
        var productWidth = parseFloat(item.getProductWidth());
        var productHeight = parseFloat(item.getProductHeight());
        var layout = computeFlatPreviewLayout(
            item.isPortrait ? productHeight : productWidth,
            item.isPortrait ? productWidth : productHeight,
            photo.naturalWidth || photo.width,
            photo.naturalHeight || photo.height,
            crop,
            PREVIEW_MAX_PX
        );

        var canvas = document.createElement('canvas');
        canvas.width = layout.outerW;
        canvas.height = layout.outerH;
        var ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Cannot composite print preview: 2D canvas context unavailable');
        }

        try {
            ctx.drawImage(photo, layout.sx, layout.sy, layout.sw, layout.sh, 0, 0, layout.outerW, layout.outerH);
            return canvasToBlob(canvas).then(function (blob) {
                return toImageFile(blob, 'preview.jpg');
            });
        } finally {
            revokeObjectUrl(objectUrl);
        }
    });
}

// Bake the retro/polaroid frame (white border, thick bottom caption band) and the user's caption text
// onto a canvas, mirroring the on-screen preview (order-image-preview.js / cropper.js). The EditServer
// photo is frameless — on screen the frame is a DOM overlay, and at manufacture it's a server-side
// postProcess step — so it must be reproduced here for the cart preview.
//
// Geometry (cm), from the product's customSettings + product dims: photo window = canvasWidth x
// canvasHeight (a square), full print = productWidth x productHeight. side border = top border =
// (printW - canvasW)/2, and the bottom caption band absorbs the remaining vertical slack. The frame
// orientation is fixed (never swapped by the photo's orientation), matching the editor.
function compositePolaroidPreview(objectUrl, item, crop) {
    var customSettings = item.getCustomSettings();
    var canvasW = parseFloat(customSettings.canvasWidth);
    var canvasH = parseFloat(customSettings.canvasHeight);
    var printW = parseFloat(item.getProductWidth());
    var printH = parseFloat(item.getProductHeight());

    // Missing/invalid dims: fall back to a plain flat preview rather than fail the whole cart line.
    if (!canvasW || !canvasH || !printW || !printH || canvasW > printW || canvasH > printH) {
        return compositeFlatPreview(objectUrl, item, crop);
    }

    return loadImage(objectUrl).then(function (photo) {
        try {
            var printAspect = printW / printH;
            var outerW = printAspect >= 1 ? PREVIEW_MAX_PX : Math.round(PREVIEW_MAX_PX * printAspect);
            var outerH = printAspect >= 1 ? Math.round(PREVIEW_MAX_PX / printAspect) : PREVIEW_MAX_PX;
            var pxPerCm = outerW / printW;

            // Photo window: centred horizontally, top border == side border (the thick band is the rest).
            var photoW = canvasW * pxPerCm;
            var photoH = canvasH * pxPerCm;
            var sideBorder = (outerW - photoW) / 2;

            // Reuse the flat-layout crop maths, but target the (square) photo-window aspect.
            var layout = computeFlatPreviewLayout(canvasW, canvasH, photo.naturalWidth || photo.width,
                photo.naturalHeight || photo.height, crop, PREVIEW_MAX_PX);

            var canvas = document.createElement('canvas');
            canvas.width = outerW;
            canvas.height = outerH;
            var ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Cannot composite polaroid preview: 2D canvas context unavailable');
            }

            // White frame, then the cropped photo drawn into its window.
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, outerW, outerH);
            ctx.drawImage(photo, layout.sx, layout.sy, layout.sw, layout.sh, sideBorder, sideBorder, photoW, photoH);

            // Light-grey hairline around the print, matching the review preview (_preview.scss).
            ctx.strokeStyle = '#DCD2D2';
            ctx.lineWidth = 1;
            ctx.strokeRect(0.5, 0.5, outerW - 1, outerH - 1);

            var text = item.options.polaroidText;
            var bandTop = sideBorder + photoH;
            if (!text || bandTop >= outerH) {
                return canvasToBlob(canvas).then(function (blob) { return toImageFile(blob, 'preview.jpg'); });
            }

            // Physical caption size (labelFontSize pt -> cm -> px), matching cropper.calculateFontSize.
            var fontCm = ((customSettings.labelFontSize || 15) / 72) * 2.54;
            var fontPx = Math.max(1, Math.round(fontCm * pxPerCm));
            return ensurePolaroidFont(fontPx).then(function () {
                ctx.fillStyle = '#000000';
                ctx.font = fontPx + 'px "Courgette", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                // maxWidth caps overflow (mirrors the label's max-width: 80%); text is already truncated
                // to one line on input, so this only guards against edge cases.
                ctx.fillText(text, outerW / 2, bandTop + (outerH - bandTop) / 2, outerW * 0.8);
                return canvasToBlob(canvas).then(function (blob) { return toImageFile(blob, 'preview.jpg'); });
            });
        } finally {
            revokeObjectUrl(objectUrl);
        }
    });
}

// Ensure the Courgette web font is loaded before drawing it to canvas — canvas fillText silently falls
// back to a system font if the face isn't ready yet. Best-effort: never blocks the preview on failure.
function ensurePolaroidFont(fontPx) {
    if (typeof document === 'undefined' || !document.fonts || !document.fonts.load) {
        return Promise.resolve();
    }
    return document.fonts.load(fontPx + 'px "Courgette"').then(function () {}, function () {});
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function loadImage(src) {
    return new Promise(function (resolve, reject) {
        var img = new Image();
        img.onload = function () {
            resolve(img);
        };
        img.onerror = function () {
            reject(new Error('Failed to load edited photo for preview compositing'));
        };
        img.src = src;
    });
}

// canvas.toBlob is polyfilled app-wide (blueimp-canvas-to-blob, required in components/app.js).
function canvasToBlob(canvas) {
    return new Promise(function (resolve, reject) {
        canvas.toBlob(function (blob) {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Failed to encode preview to JPEG'));
            }
        }, 'image/jpeg', 0.9);
    });
}

function revokeObjectUrl(objectUrl) {
    var urlCreator = window.URL || window.webkitURL;
    if (urlCreator && urlCreator.revokeObjectURL) {
        urlCreator.revokeObjectURL(objectUrl);
    }
}

// Scale `prep` down to PREVIEW_MAX_PX instead of dropping it: keeps a non-empty filter so
// getSrcWithFilter routes through the EditServer (blob URL) rather than short-circuiting to the raw
// image URL, and preserves crop/rotate/effects/border at preview size.
function buildPreviewFilter(orderFilter) {
    // Clone: never mutate the order's filter.
    var filter = JSON.parse(JSON.stringify(orderFilter));

    // Don't send crop to the EditServer (like the on-screen editor); compositeFlatPreview crops. Avoids
    // a double-crop if the server honours it.
    delete filter.crop;

    if (filter.prep) {
        var w = filter.prep.w || 0;
        var h = filter.prep.h || 0;
        var longest = Math.max(w, h);

        if (longest > PREVIEW_MAX_PX) {
            var scale = PREVIEW_MAX_PX / longest;
            filter.prep.w = Math.round(w * scale);
            filter.prep.h = Math.round(h * scale);
            if (filter.prep.imageBorder && filter.prep.imageBorder.w) {
                filter.prep.imageBorder.w = Math.round(filter.prep.imageBorder.w * scale);
            }
        }
    }

    return filter;
}

// Wrap a Blob as a named File; fall back to a named Blob where File isn't constructable.
function toImageFile(blob, name) {
    var type = blob.type || 'image/jpeg';
    try {
        return new File([blob], name, { type: type });
    } catch (e) {
        blob.name = name;
        return blob;
    }
}
